import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { BarChart3, BookOpen, CheckCircle2, ChevronRight, Compass, Flame, MapPin, Moon, Settings, Sun, Target, User } from 'lucide-react';
import { PRAYER_LABEL, dayKeyIn, fmtTime, hijriOn, nextPrayer, qiblaBearing } from '@shared/prayer';
import { LocationSheet, QiblaSheet } from '@/features/prayer/Prayer';
import { alive, useProgress, useSettings } from '@/lib/store';
import { chapter, parseKey } from '@/features/quran/api';
import { useAdhkarProgress } from '@/features/adhkar/Adhkar';
import { useLastDhikr } from '@/features/adhkar/DhikrCounter';
import { bestStreak, goalProgress, secondsOn, streak } from '@/features/progress/stats';
import { STREAKS, streakFor } from '@/features/prayer/prayerMetrics';
import { periodOn } from '@/features/fasting/fastMetrics';
import { Card, Ring } from '@/components/ui';
import { InstallBanner } from '@/app/InstallBanner';
import { khatmahProgress, sumDays } from '@/features/khatmah/khatmah';
import { PrayerTimer } from '@/features/prayer/PrayerTimer';
import { phaseAt } from '@/components/islamic/Scene';
import { Backdrop } from '@/components/islamic/Backdrop';
import { LanternIcon, TasbihIcon } from '@/components/islamic/Ornaments';
import { fastingInfo, upcomingFasts } from '@shared/fasting';
import { qadaSummary } from '@/features/fasting/fastMetrics';
import { ContinueListening } from '@/features/player/ResumeUI';
import { SECONDS_WITHIN_MS, currentWindow, fmtUntil } from '@/features/prayer/window';
import { useSession } from '@/lib/auth';
import { addDays } from '@shared/prayer';

/** First name from the account (sign-up name; falls back to the email's local part). */
/** Hyphenated names stay whole (Abdul-Azeez, not Abdul), each part capitalised. */
export function firstNameOf(name?: string | null) {
  const first = name?.trim().split(/[\s@._]+/)[0].replace(/^-+|-+$/g, '');
  return first ? first.split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('-') : null;
}

function greeting(h: number) {
  if (h < 5) return 'Peaceful night';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export function Home() {
  const { prayer, hour12, hijriOffset, dailyGoalMinutes } = useSettings((s) => s.settings);
  const [now, setNow] = useState(() => new Date());
  const prayerSettings = useSettings((s) => s.settings.prayer);
  // Tick every second in the last 15 minutes before a prayer (the countdown shows seconds), else every 30 s.
  useEffect(() => {
    const close = nextPrayer(prayerSettings, now).at.getTime() - now.getTime() <= SECONDS_WITHIN_MS + 30_000;
    const t = setTimeout(() => setNow(new Date()), close ? 1000 : 30_000);
    return () => clearTimeout(t);
  }, [now, prayerSettings]);
  const today = dayKeyIn(prayer.tz, now);
  const next = nextPrayer(prayer, now);
  const hijri = hijriOn(today, hijriOffset);
  const lastRead = useProgress((s) => s.lastRead);
  const sessions = alive(useProgress((s) => s.sessions));
  const goal = alive(useProgress((s) => s.goals)).find((g) => g.active);
  const gp = goal ? goalProgress(goal, sessions, today) : null;
  const minutes = Math.floor(secondsOn(sessions, today) / 60);
  const ring = gp ? gp.today : minutes / dailyGoalMinutes;
  const fasts = useProgress((s) => s.fasts);
  const periods = useProgress((s) => s.periods);
  const fastToday = fastingInfo(today, hijriOffset);
  const nextFast = fastToday.rules.some((r) => r.strength !== 'recommended') ? null : upcomingFasts(addDays(today, 1), 14, hijriOffset)[0];
  const owed = useMemo(() => qadaSummary(fasts, periods, today, hijriOffset).remaining, [fasts, periods, today, hijriOffset]);
  const plans = alive(useProgress((s) => s.khatmahs)).sort((a, b) => a.createdAt - b.createdAt);
  const ayahToday = useProgress((s) => s.ayahLog[today]);
  const ayahLog = useProgress((s) => s.ayahLog);
  const lifetime = useMemo(() => sumDays(Object.values(ayahLog)), [ayahLog]);
  const prayers = useProgress((s) => s.prayers);
  const gender = useSettings((s) => s.settings.gender);
  /* Prayer streak: all five prayed; period days neither count nor break it (same rule as Prayer stats). */
  const prayerStreak = useMemo(() => streakFor(prayers, STREAKS[0], today, gender === 'female' ? (d) => !!periodOn(periods, d, today) : undefined), [prayers, periods, gender, today]);
  /* Qurʾan streak: any day with reading/listening time or āyāt read in a khatmah. */
  const quranStreak = useMemo(() => {
    const days = new Set(sessions.filter((s) => s.kind !== 'adhkar').map((s) => s.dayKey));
    for (const d of Object.values(ayahLog)) if (d.ayahs > 0) days.add(d.dayKey);
    return { current: streak(today, (d) => days.has(d)).current, best: bestStreak([...days]) };
  }, [sessions, ayahLog, today]);
  const morning = useAdhkarProgress('morning');
  const evening = useAdhkarProgress('evening');
  const lastDhikr = useLastDhikr();
  const until = fmtUntil(next.at.getTime() - now.getTime());
  // A prayer's time is running (Fajr until sunrise, Dhuhr until ʿAṣr, …) — it leads; the next one is secondary.
  const inWindow = !!currentWindow(prayer, now);
  const place = (prayer.placeName ?? '').split(',')[0];
  const [locOpen, setLocOpen] = useState(false);
  const [qiblaOpen, setQiblaOpen] = useState(false);
  const { data: session } = useSession();
  const displayName = useSettings((s) => s.settings.displayName);
  const firstName = firstNameOf(session?.user?.name || displayName);

  return (
    <div className="page-pad">
      {/* Hero: the sky over the masjid follows the prayer day */}
      <div className="relative mx-3 mb-4 overflow-hidden rounded-[28px] shadow-[0_20px_50px_-28px_rgba(0,0,0,.8)]" style={{ marginTop: 'calc(var(--safe-top) * -1 + 8px)' }}>
        {/* A little shorter on phones (≈380px on an iPhone), full height on wider screens. */}
        <Backdrop phase={phaseAt(prayer, now)} height="clamp(380px, 96vw, 440px)" lift={150} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/30" />
        {/* Date + buttons on the top row; the greeting gets the full width below, so it doesn't wrap on phones. */}
        <div className="absolute inset-x-0 top-0 p-4 text-white" style={{ paddingTop: 'calc(var(--safe-top) + 6px)' }}>
          <div className="flex items-center justify-between gap-3">
            <div className="text-[12px] font-semibold uppercase tracking-wider text-white/80">{hijri ? `${hijri.d} ${hijri.month} ${hijri.y} AH` : ''}</div>
            <div className="flex gap-2">
              <Link to="/progress" aria-label="Progress" className="press grid h-10 w-10 place-items-center rounded-full bg-white/15 backdrop-blur-md"><BarChart3 size={18} /></Link>
              <Link to="/account" aria-label="Account" className="press grid h-10 w-10 place-items-center rounded-full bg-white/15 backdrop-blur-md"><User size={18} /></Link>
              <Link to="/settings" aria-label="Settings" className="press grid h-10 w-10 place-items-center rounded-full bg-white/15 backdrop-blur-md"><Settings size={18} /></Link>
            </div>
          </div>
          <h1 className="mt-1 text-[24px] font-bold leading-tight drop-shadow sm:text-[28px]">As-salāmu ʿalaykum{firstName ? <>, <span className="whitespace-nowrap">{firstName}</span></> : ''}</h1>
          <div className="w-fit whitespace-nowrap font-kufi text-[16px] leading-snug text-[#f3d27a] sm:text-[18px]" dir="rtl" lang="ar">السلام عليكم ورحمة الله وبركاته</div>
          <div className="text-[13px] font-medium text-white/80">{greeting(now.getHours())}</div>
        </div>
        <Link to="/prayer" className="absolute inset-x-3 bottom-3 block rounded-2xl bg-black/35 p-3.5 text-white backdrop-blur-md">
          {inWindow ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <span className="truncate pt-0.5 text-[12px] font-semibold uppercase tracking-wider text-white/75">Now · {place}</span>
                <div className="shrink-0 text-right leading-tight">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-white/65">Next</div>
                  <div className="text-[15px] font-semibold">{PRAYER_LABEL[next.key].en} <span className="tabular-nums text-white/85">{fmtTime(next.at, prayer.tz, hour12)}</span></div>
                  <div className="text-[12px] font-semibold text-[#9ff0c9]">in {until}</div>
                </div>
              </div>
              <div className="mt-1 [&_.text-muted]:text-white/70 [&_[role=status]_.truncate]:text-[18px]"><PrayerTimer settings={prayer} now={now} hour12={hour12} compact showLeft={false} /></div>
            </>
          ) : (
            <>
              <div className="flex items-baseline justify-between">
                <span className="text-[12px] font-semibold uppercase tracking-wider text-white/75">Next · {place}</span>
                <span className="text-[13px] font-semibold text-[#9ff0c9]">in {until}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-[26px] font-bold">{PRAYER_LABEL[next.key].en} <span className="font-arabic text-[20px] font-normal text-[#f3d27a]">{PRAYER_LABEL[next.key].ar}</span></span>
                <span className="text-[22px] font-semibold tabular-nums">{fmtTime(next.at, prayer.tz, hour12)}</span>
              </div>
            </>
          )}
        </Link>
      </div>

      {/* Location and Qibla, one tap each — prayer times follow the location. */}
      <div className="mb-3 grid grid-cols-2 gap-3 px-4">
        <button type="button" onClick={() => setLocOpen(true)} aria-label="Change location" className="press flex min-w-0 items-center gap-2.5 rounded-2xl bg-surface px-3.5 py-3 text-left">
          <MapPin size={18} className="shrink-0 text-accent" />
          <span className="min-w-0">
            <span className="block text-[12px] font-semibold uppercase tracking-wide text-muted">Location</span>
            <span className="block truncate text-[15px] font-semibold">{place || 'Set location'}</span>
          </span>
        </button>
        <button type="button" onClick={() => setQiblaOpen(true)} aria-label="Qibla direction" className="press flex min-w-0 items-center gap-2.5 rounded-2xl bg-surface px-3.5 py-3 text-left">
          <Compass size={18} className="shrink-0 text-gold" />
          <span className="min-w-0">
            <span className="block text-[12px] font-semibold uppercase tracking-wide text-muted">Qibla</span>
            <span className="block truncate text-[15px] font-semibold tabular-nums">{Math.round(qiblaBearing(prayer.lat, prayer.lng))}° from North</span>
          </span>
        </button>
      </div>
      <LocationSheet open={locOpen} onClose={() => setLocOpen(false)} />
      <QiblaSheet open={qiblaOpen} onClose={() => setQiblaOpen(false)} />

      <InstallBanner />

      <div className="grid gap-3 px-4">
        <ContinueListening />
        <div className="grid grid-cols-2 gap-3">
          {([['Prayer streak', prayerStreak, '/prayer/stats', 'all 5 prayed'], ["Qur'an streak", quranStreak, '/progress', 'any reading']] as const).map(([label, s, to, rule]) => (
            <Card key={label} to={to} className="flex items-center gap-3 p-4">
              <Flame size={26} className={s.current ? 'shrink-0 text-[#ff9f0a]' : 'shrink-0 text-faint'} aria-hidden />
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</div>
                <div className="text-[20px] font-bold tabular-nums leading-tight">{s.current} <span className="text-[12px] font-medium text-muted">{s.current === 1 ? 'day' : 'days'}</span></div>
                <div className="truncate text-[11px] text-muted">best {s.best} · {rule}</div>
              </div>
            </Card>
          ))}
        </div>
        <Card to={goal ? '/progress' : '/progress?goal=new'} className="flex items-center gap-4 p-5">
          <Ring value={ring} size={84} stroke={9}><span className="text-[18px] font-bold tabular-nums">{minutes}<span className="text-[11px] font-medium text-muted">m</span></span></Ring>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Today's goal</div>
            <div className="text-[16px] font-semibold">{goal ? goal.title : `${dailyGoalMinutes} min with the Qur'an`}</div>
            <div className="text-[13px] text-muted">{gp ? gp.todayLabel : `${minutes} / ${dailyGoalMinutes} min`}</div>
            {!goal && <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[12px] font-semibold text-accent-ink"><Target size={12} /> Set a reading goal</div>}
          </div>
        </Card>
      </div>

      <div className="px-4 pt-3">
        <Card to="/khatmah" className="p-4">
          <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Khatmah</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="rounded-2xl bg-fill px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">Today</div>
              <div className="text-[17px] font-bold tabular-nums">{(ayahToday?.ayahs ?? 0).toLocaleString('en-GB')} <span className="text-[12px] font-medium text-muted">āyāt · {(ayahToday?.pages ?? 0).toLocaleString('en-GB')} pages</span></div>
              <div className="text-[13px] font-semibold tabular-nums text-gold">{(ayahToday?.hasanat ?? 0).toLocaleString('en-GB')} ḥasanāt</div>
            </div>
            <div className="rounded-2xl bg-gold/10 px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">All time</div>
              <div className="text-[17px] font-bold tabular-nums">{lifetime.ayahs.toLocaleString('en-GB')} <span className="text-[12px] font-medium text-muted">āyāt · {lifetime.pages.toLocaleString('en-GB')} pages</span></div>
              <div className="text-[13px] font-semibold tabular-nums text-gold">{lifetime.hasanat.toLocaleString('en-GB')} ḥasanāt</div>
            </div>
          </div>
          {plans.length ? plans.slice(0, 4).map((k) => {
            const p = khatmahProgress(k, today);
            const pct = p.dailyTarget ? Math.min(1, p.todayDone / p.dailyTarget) : p.pct;
            return (
              <div key={k.id} className="mt-2.5">
                <div className="flex justify-between text-[14px]">
                  <span className="flex min-w-0 items-center gap-1.5 font-medium"><span className="truncate">{k.title}</span>{p.doneToday && <CheckCircle2 size={16} className="shrink-0 text-[var(--win-ok)]" aria-label="Done for today" />}</span>
                  <span className={`shrink-0 pl-2 tabular-nums ${p.doneToday ? 'font-semibold text-[var(--win-ok)]' : 'text-muted'}`}>{p.doneToday ? 'done today' : p.dailyTarget ? `${p.todayDone}/${p.dailyTarget} today` : `${Math.round(p.pct * 100)}%`}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-fill"><div className={`h-full rounded-full ${p.doneToday ? 'bg-[var(--win-ok)]' : 'bg-accent'}`} style={{ width: `${pct * 100}%` }} /></div>
              </div>
            );
          }) : <div className="mt-1 text-[15px] font-semibold">Start a khatmah plan — one āyah at a time</div>}
        </Card>
      </div>

      <div className="px-4 pt-3">
        <Card to={lastRead ? `/quran/page/${lastRead.page}` : '/quran/1'} className="flex items-center gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-accent text-accent-ink"><BookOpen size={22} /></span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">{lastRead ? 'Continue reading' : 'Start reading'}</div>
            <div className="truncate text-[17px] font-bold">{lastRead ? `${chapter(parseKey(lastRead.verseKey).surah).n} · page ${lastRead.page}` : 'Al-Fātiḥah'}</div>
          </div>
          <ChevronRight className="text-faint" />
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-3 px-4 pt-3">
        <Card to="/adhkar/morning" className="flex items-center gap-3">
          <Ring value={morning.done / morning.total} size={44} stroke={5}><Sun size={18} className="text-gold" /></Ring>
          <div><div className="font-semibold">Morning</div><div className="text-[12px] text-muted">{morning.complete ? 'Done ✓' : `${morning.done}/${morning.total}`}</div></div>
        </Card>
        <Card to="/adhkar/evening" className="flex items-center gap-3">
          <Ring value={evening.done / evening.total} size={44} stroke={5}><Moon size={17} className="text-gold" /></Ring>
          <div><div className="font-semibold">Evening</div><div className="text-[12px] text-muted">{evening.complete ? 'Done ✓' : `${evening.done}/${evening.total}`}</div></div>
        </Card>
      </div>

      {/* The dhikr counted last — one tap to carry on counting it. */}
      <div className="px-4 pt-3">
        {lastDhikr ? (
          <Card to={`/adhkar/dhikr/${lastDhikr.dhikr.id}`} className="flex items-center gap-4">
            <Ring value={lastDhikr.target > 0 ? Math.min(1, lastDhikr.today / lastDhikr.target) : lastDhikr.today ? 1 : 0} size={52} stroke={5}>
              <span className="text-[13px] font-bold tabular-nums">{lastDhikr.today > 999 ? '999+' : lastDhikr.today}</span>
            </Ring>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Dhikr</div>
              {lastDhikr.dhikr.ar && <div className="truncate font-arabic text-[19px] leading-snug" dir="rtl" lang="ar">{lastDhikr.dhikr.ar}</div>}
              <div className="truncate text-[13px] text-muted">{lastDhikr.dhikr.tr} · {lastDhikr.today.toLocaleString('en-GB')}{lastDhikr.target > 0 ? ` / ${lastDhikr.target}` : ''} today</div>
            </div>
            <ChevronRight className="text-faint" />
          </Card>
        ) : (
          <Card to="/adhkar/dhikr" className="flex items-center gap-4">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-accent/15 text-accent"><TasbihIcon size={26} /></span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Dhikr counter</div>
              <div className="truncate text-[16px] font-semibold">Count istighfār, tasbīḥ and more</div>
            </div>
            <ChevronRight className="text-faint" />
          </Card>
        )}
      </div>

      <div className="px-4 pt-3">
        <Card to="/fasting" className="flex items-center gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gold/20 text-gold">{fastToday.ramadan ? <LanternIcon size={24} /> : <Moon size={22} />}</span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Fasting</div>
            <div className="truncate text-[16px] font-semibold">
              {fastToday.forbidden ? `${fastToday.forbidden} — no fasting today`
                : fastToday.rules[0] && fastToday.rules[0].strength !== 'recommended' ? `Today: ${fastToday.rules[0].label}`
                  : nextFast ? `Next: ${nextFast.rules.find((r) => r.strength !== 'recommended')!.label} · ${new Date(`${nextFast.dayKey}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })}`
                    : 'Log a voluntary or make-up fast'}
            </div>
            {owed > 0 && <div className="text-[13px] text-muted">{owed} Ramadan fast{owed === 1 ? '' : 's'} to make up</div>}
          </div>
          <ChevronRight className="text-faint" />
        </Card>
      </div>

    </div>
  );
}
